/**
 * Visual Blocks Language
 *
 * Copyright 2012 Google Inc.
 * http://blockly.googlecode.com/
 * and 2014 Massachusetts Institute of Technology
 * http://zerorobotics.org/
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * @fileoverview Generating C++ for math blocks.
 *
 * Modified from the standard Blockly JavaScript generator.
 * Converted from the legacy Zero Robotics Blockly generator
 * to the modern STEMX365 zrCppGenerator.
 *
 * @author q.neutron@gmail.com (Quynh Neutron)
 * @author dininno@mit.edu (Ethan DiNinno)
 */

import { zrCppGenerator } from "./zrCppGenerator";


// ============================================================
// math_number
// ============================================================

zrCppGenerator.forBlock["math_number"] =
    function(block) {

        // Numeric value.
        let code =
            block.getFieldValue("NUM");

        // Validate that the input starts with a number.
        // parseFloat correctly ignores the trailing f
        // on single-precision floats.
        if (isNaN(parseFloat(code))) {
            code = "0";
        }

        return [
            code,
            this.ORDER_ATOMIC
        ];
    };


// ============================================================
// math_arithmetic
// ============================================================

zrCppGenerator.forBlock["math_arithmetic"] =
    function(block) {

        // Basic arithmetic operators, and power.
        const OPERATORS = {
            ADD: [
                " + ",
                this.ORDER_ADDITION
            ],

            MINUS: [
                " - ",
                this.ORDER_SUBTRACTION
            ],

            MULTIPLY: [
                " * ",
                this.ORDER_MULTIPLICATION
            ],

            DIVIDE: [
                " / ",
                this.ORDER_DIVISION
            ],

            POWER: [
                null,
                this.ORDER_COMMA
            ]
        };

        const tuple =
            OPERATORS[
                block.getFieldValue("OP")
            ];

        const operator = tuple[0];
        const order = tuple[1];

        const argument0 =
            this.valueToCode(
                block,
                "A",
                order
            ) || "0";

        const argument1 =
            this.valueToCode(
                block,
                "B",
                order
            ) || "0";

        // Power requires a special case since
        // it has no C++ operator.
        //
        // The ZR libraries use single-precision floats.
        if (!operator) {

            const code =
                "powf(" +
                argument0 +
                ", " +
                argument1 +
                ")";

            return [
                code,
                this.ORDER_FUNCTION_CALL
            ];
        }

        const code =
            argument0 +
            operator +
            argument1;

        return [
            code,
            order
        ];
    };


// ============================================================
// math_single
// ============================================================

zrCppGenerator.forBlock["math_single"] =
    function(block) {

        // Math operators with single operand.
        const operator =
            block.getFieldValue("OP");

        let code;
        let arg;

        // ----------------------------------------------------
        // Negation
        // ----------------------------------------------------

        if (operator === "-") {

            // Negation is a special case because
            // of its different operator precedence.
            arg =
                this.valueToCode(
                    block,
                    "NUM",
                    this.ORDER_UNARY_NEGATION
                ) || "0";

            // --3 is not legal C++.
            if (arg[0] === "-") {
                arg = " " + arg;
            }

            code =
                "-" +
                arg;

            return [
                code,
                this.ORDER_UNARY_NEGATION
            ];
        }

        // ----------------------------------------------------
        // Other single-argument math functions
        // ----------------------------------------------------

        arg =
            this.valueToCode(
                block,
                "NUM",
                this.ORDER_NONE
            ) || "0";

        // ZR trig functions are single-precision
        // and operate in radians.
        code =
            operator +
            "(" +
            arg +
            ")";

        return [
            code,
            this.ORDER_FUNCTION_CALL
        ];
    };


// ============================================================
// math_constant
// ============================================================

zrCppGenerator.forBlock["math_constant"] =
    function(block) {

        return [
            block.getFieldValue("CONSTANT"),
            this.ORDER_ATOMIC
        ];
    };


// ============================================================
// math_change
// ============================================================

zrCppGenerator.forBlock["math_change"] =
    function(block) {

        // Add to a variable in place.

        const argument0 =
            this.valueToCode(
                block,
                "DELTA",
                this.ORDER_ADDITION
            ) || "0";

        const varName =
            this.valueToCode(
                block,
                "VAR",
                this.ORDER_ADDITION
            ) || "0";

        return (
            varName +
            " += " +
            argument0 +
            ";\n"
        );
    };


// ============================================================
// math_round
// ============================================================

// Rounding functions have a single operand.
zrCppGenerator.forBlock["math_round"] =
    zrCppGenerator.forBlock["math_single"];


// ============================================================
// math_trig
// ============================================================

// Trigonometry functions have a single operand.
zrCppGenerator.forBlock["math_trig"] =
    zrCppGenerator.forBlock["math_single"];


// ============================================================
// math_modulo
// ============================================================

zrCppGenerator.forBlock["math_modulo"] =
    function(block) {

        // Remainder computation.

        const argument0 =
            this.valueToCode(
                block,
                "DIVIDEND",
                this.ORDER_MODULUS
            ) || "0";

        const argument1 =
            this.valueToCode(
                block,
                "DIVISOR",
                this.ORDER_MODULUS
            ) || "0";

        const code =
            argument0 +
            " % " +
            argument1;

        return [
            code,
            this.ORDER_MODULUS
        ];
    };